import { getClassLevel } from './classIdentity.mjs';

// 2024 Bard (SRD 5.2.1). Jack of All Trades lives in classExpertise.js, since the skill bonuses reading it are synchronous.
// The Bard's uses are saved at `classState.bard.inspiration` as uses spent, so a new Charisma score grows the remainder.
// The die a creature holds is saved on that creature: `bardicInspiration = { has, sides, fromId, fromName, roundsLeft,
// revision, requestId?, kind?, rolls? }`. Only player characters hold one, since a roll prompt needs a player to answer it.
const BARD_FEATURE_LEVELS = Object.freeze({ fontOfInspiration: 5, countercharm: 7, magicalSecrets: 10, superiorInspiration: 18, wordsOfCreation: 20 });
export const getBardLevel = character => getClassLevel(character, 'bard');
export const hasBardFeature = (character, feature) => getBardLevel(character) >= (BARD_FEATURE_LEVELS[feature] ?? Infinity);

export const BARDIC_INSPIRATION_FEATURE_ID = 'bard:bardic-inspiration';
export const FONT_OF_INSPIRATION_FEATURE_ID = 'bard:font-of-inspiration';
export const BARDIC_INSPIRATION_RANGE_FEET = 60;
/** The die lasts an hour. Only time passed by a rest or the DM counts it down: one fight doesn't reach 600 rounds. */
export const BARDIC_INSPIRATION_ROUNDS = 600;

export const bardicDieSides = level => (level >= 15 ? 12 : level >= 10 ? 10 : level >= 5 ? 8 : 6);

/** Uses equal the Charisma modifier, minimum 1. */
export const getBardicInspirationProfile = (character, charismaModifier) => {
  const level = getBardLevel(character);
  const maximum = level ? Math.max(1, Math.floor(Number(charismaModifier) || 0)) : 0;
  const state = character?.classState?.bard?.inspiration || {};
  const spent = Math.min(maximum, Math.max(0, Math.floor(Number(state.spent) || 0)));
  return { level, sides: bardicDieSides(level), maximum, spent, remaining: maximum - spent, revision: Math.max(0, Number(state.revision) || 0) };
};

/** All uses return on a Long Rest, and on a Short Rest from level 5 (Font of Inspiration); null means nothing changes. */
export const bardicInspirationRestState = (character, type) => {
  if (!getBardLevel(character) || !(type === 'long' || (type === 'short' && hasBardFeature(character, 'fontOfInspiration')))) return null;
  const state = character?.classState?.bard?.inspiration;
  return Number(state?.spent) > 0 ? { spent: 0, revision: (Number(state.revision) || 0) + 1 } : null;
};

/** Superior Inspiration (18): rolling Initiative brings the uses up to two, if the Bard has fewer. Null means no change. */
export const superiorInspirationState = (character, charismaModifier) => {
  const profile = getBardicInspirationProfile(character, charismaModifier);
  const spent = Math.max(0, profile.maximum - 2);
  return hasBardFeature(character, 'superiorInspiration') && profile.remaining < 2 && spent < profile.spent ? { spent, revision: profile.revision + 1 } : null;
};

/** Merge a server state unless the sheet already holds a newer revision. */
export const applyBardicInspirationUpdate = (character, inspiration) => {
  if (!inspiration || Number(character?.classState?.bard?.inspiration?.revision || 0) > Number(inspiration.revision || 0)) return character;
  return { ...character, classState: { ...character?.classState, bard: { ...character?.classState?.bard, inspiration } } };
};

export const hasBardicDie = character => character?.bardicInspiration?.has === true;
export const heldBardicDie = character => (hasBardicDie(character) ? character.bardicInspiration : null);
/** The holder's state after `rounds` pass, or null when it still holds the same die or holds none. */
export const bardicDieAfterTime = (character, rounds) => {
  const held = heldBardicDie(character);
  if (!held) return null;
  const roundsLeft = (Number(held.roundsLeft) || 0) - rounds;
  return roundsLeft > 0 ? { ...held, roundsLeft, revision: (Number(held.revision) || 0) + 1 } : { has: false, revision: (Number(held.revision) || 0) + 1 };
};
/** The character with the die's new state, unless it already holds a newer one. */
export const withBardicDie = (character, bardicInspiration) => (character && bardicInspiration
  && Number(bardicInspiration.revision || 0) >= Number(character.bardicInspiration?.revision || 0)
  ? { ...character, bardicInspiration } : character);

// Countercharm (7): a failed save against an effect that applies Charmed or Frightened can be rerolled with Advantage. Saves
// to end a condition don't count. The server's countercharm.js decides which saves qualify.
export const COUNTERCHARM_FEATURE_ID = 'bard:countercharm';
export const COUNTERCHARM_RANGE_FEET = 30;
const CHARM_OR_FEAR = Object.freeze(['charmed', 'frightened']);
export const appliesCharmOrFear = conditions => (conditions || []).some(condition => CHARM_OR_FEAR.includes(condition));
/** The d20 a reroll keeps from `rolls` (two dice): Advantage, unless Disadvantage cancels it and one die stands. */
export const countercharmDie = (rolls, rollMode) => (rollMode === 'disadvantage' ? rolls[0] : Math.max(...rolls));

// Magical Secrets (10): prepared spells can come from these lists too and count as Bard spells. Cantrips stay on the Bard list.
const MAGICAL_SECRETS_LISTS = Object.freeze(['bard', 'cleric', 'druid', 'wizard']);
/** Whether this character can prepare `spell` as a Bard spell. */
export const isBardSpellFor = (character, spell) => {
  const lists = hasBardFeature(character, 'magicalSecrets') && Number(spell?.level) >= 1 ? MAGICAL_SECRETS_LISTS : ['bard'];
  return (spell?.classes || []).some(entry => lists.includes(String(entry).toLowerCase()));
};

/** Which other list a Magical Secrets spell comes from, such as "Wizard list" or "Cleric and Druid lists"; '' for a Bard spell. */
export const magicalSecretsLabel = spell => {
  const lists = (spell?.classes || []).map(entry => String(entry).toLowerCase());
  if (lists.includes('bard')) return '';
  const names = MAGICAL_SECRETS_LISTS.filter(list => lists.includes(list)).map(list => `${list[0].toUpperCase()}${list.slice(1)}`);
  return names.length ? `${names.join(names.length > 2 ? ', ' : ' and ')} ${names.length > 1 ? 'lists' : 'list'}` : '';
};

// Words of Creation (20): both spells are always prepared, through feature grants with Bard slots and Charisma, and either
// can take a second creature within 10 feet of the first, however the spell was prepared.
export const WORDS_OF_CREATION_SPELLS = Object.freeze(['power-word-heal', 'power-word-kill']);
export const WORDS_OF_CREATION_REACH_FEET = 10;
export const WORDS_GRANT_PREFIX = 'bard-words-';
export const wordsGrantId = spellId => `${WORDS_GRANT_PREFIX}${spellId}`;
export const wordsSpellForGrant = grantId => (String(grantId || '').startsWith(WORDS_GRANT_PREFIX) ? String(grantId).slice(WORDS_GRANT_PREFIX.length) : null);
export const getWordsOfCreationSpells = character => (hasBardFeature(character, 'wordsOfCreation') ? WORDS_OF_CREATION_SPELLS : []);
export const wordsOfCreationTargets = (character, spellId) => (getWordsOfCreationSpells(character).includes(spellId) ? 2 : 1);

export const bardicDieLabel = held => `+d${held.sides} · Bardic Inspiration (from ${held.fromName})`;
export const bardicDieDecision = (resolutionId, what, held) => ({
  id: `${resolutionId}:${BARDIC_INSPIRATION_FEATURE_ID}`, featureId: BARDIC_INSPIRATION_FEATURE_ID, title: 'Bardic Inspiration',
  prompt: `Your ${what} missed. Roll the d${held.sides} from ${held.fromName} and add it to the roll?`,
  choices: [{ id: 'use', label: `Add the d${held.sides}`, action: 'apply' }, { id: 'skip', label: 'Keep the roll' }],
});
